import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import { TFullTaskData, TTaskItem, TTaskSource, TTaskStatus, TTaskType } from '../hooks/dto/taskItem.dto';
import { Status } from '@src/services/brain/modules/v1/tasks/dto/brain-tasks.get.response.dto';
import {
  TaskItemDTO as GroupTaskItemDTO,
  TaskGroupItemDTO,
} from '@src/services/brain/modules/v1/tasks/dto/brain-tasks.getGroups.response.dto';
import { TasksByIdMapper } from '../hooks/tasks.mapper';
import { getTeamMembers, getUsersNames } from '../hooks/users';
import { getOrganizationsNames } from '../hooks/organizations';
import { useAppSelector } from '@src/redux/hooks';
import { GetTasksSummaryResponseDTO } from '@src/services/brain/modules/v1/tasks/dto/brain-tasks.getSummary.response.dto';
import { TeamMemberItemDTO } from '@src/services/brain/modules/v1/team-members/dto/brain-team-members.get.response.dto';
import { getDefaultSourceFiltersBySquads } from '../config/squad-filters.config';
import {
  getTasks,
  getTasksGroups,
  updateTaskStatus,
  updateTaskAssignedUser,
  TaskFilters,
  getTaskById,
  getTasksSummary,
  updateBulkTasks,
  createTaskComment,
  updateTaskComment,
  deleteTaskComment,
  createTaskRerun,
  getTaskContext,
} from '../hooks/tasks';
import { RequestGetByIdResponseDTO } from '@src/services/automations/modules/v1/requests/dto/requests.getById.response.dto';

export interface TeamMemberOption {
  id: string;
  name: string;
  email: string;
}

export interface TaskGroup {
  name: string;
  message: string;
  source: TTaskSource;
  type: TTaskType;
  organizationId: string;
  statusCounts: Record<TTaskStatus, number>;
  totalTasks: number;
  tasks: GroupTaskItemDTO[];
}

interface TasksContextValue {
  tasks: TTaskItem[];
  isLoading: boolean;

  filters: TaskFilters;
  messageFilter: string;
  sourceFilter: string[];
  typeFilter: string[];
  statusFilter: string[];
  organizationFilter: string;
  handleFilterChange: (property: keyof TTaskItem, value: string | string[]) => void;
  handleMessageFilterChange: (value: string) => void;
  handleSourceFilterChange: (value: string[]) => void;
  handleTypeFilterChange: (value: string[]) => void;
  handleStatusFilterChange: (value: string[]) => void;
  handleOrganizationFilterChange: (organizationId: string | null) => void;
  handleSortChange: (sortOrder: 'asc' | 'desc') => void;

  startDate: Date | null;
  endDate: Date | null;
  setStartDate: (date: Date | null) => void;
  setEndDate: (date: Date | null) => void;

  currentPage: number;
  totalPages: number;
  handlePageChange: (page: number) => void;

  handleUpdateTaskStatus: (taskId: string, status: Status) => void;
  handleUpdateTaskUser: (taskId: string, userId?: string | null, toActualUser?: boolean) => void;
  handleTaskClick: (task: TTaskItem) => void;
  handleBulkUpdateTasks: (payload: {
    taskIds: string[];
    newStatus?: string;
    newAssignedUserId?: string;
  }) => Promise<void>;

  selectedTaskId: string | null;
  selectedTask: TFullTaskData | null;
  isLoadingTask: boolean;

  taskContext: RequestGetByIdResponseDTO | null;
  isLoadingTaskContext: boolean;

  handleCreateComment: (content: string) => Promise<void>;
  handleUpdateComment: (commentId: string, content: string) => Promise<void>;
  handleDeleteComment: (commentId: string) => Promise<void>;

  handleCreateRerun: () => Promise<void>;

  userNames: Record<string, string>;
  teamMembers: TeamMemberOption[];
  organizationNames: Record<string, string>;
  isLoadingOrganizations: boolean;

  tasksSummary: GetTasksSummaryResponseDTO | null;

  tasksError: boolean;
  groupsError: boolean;
  retryTasks: () => void;
  retryGroups: () => void;

  newTasksCount: number;
  handleLoadNewTasks: () => void;

  selectedGroup: TaskGroup | null;
  handleGroupSelect: (group: TaskGroup | null) => void;
  clearSelectedTask: () => void;

  taskGroups: TaskGroup[];
  isLoadingGroups: boolean;
  groupsCurrentPage: number;
  groupsTotalPages: number;
  handleGroupsPageChange: (page: number) => void;
}

const calculateStatusCounts = (tasks: GroupTaskItemDTO[]): Record<TTaskStatus, number> => {
  const counts: Record<TTaskStatus, number> = {
    PENDING: 0,
    IN_PROGRESS: 0,
    CONCLUDED: 0,
    REJECTED: 0,
  };

  tasks.forEach((task) => {
    if (task.status) {
      const mappedStatus = task.status;
      if (mappedStatus) {
        counts[mappedStatus]++;
      }
    }
  });

  return counts;
};

const TasksContext = createContext<TasksContextValue | null>(null);

export const useTasksContext = (): TasksContextValue => {
  const context = useContext(TasksContext);
  if (!context) {
    throw new Error('useTasksContext must be used within a TasksProvider');
  }
  return context;
};

export const TasksProvider = ({ children }: { children: ReactNode }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [tasks, setTasks] = useState<TTaskItem[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  });
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  const [teamMembers, setTeamMembers] = useState<TeamMemberItemDTO[]>([]);
  const [isLoadingTeamMembers, setIsLoadingTeamMembers] = useState(true);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [organizationNames, setOrganizationNames] = useState<Record<string, string>>({});
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(true);
  const [filters, setFilters] = useState<TaskFilters>({
    sortBy: 'createdAt',
    sortOrder: 'desc',
    type: ['ERROR'],
  });
  const [messageFilter, setMessageFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<string[]>(['ERROR']);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [organizationFilter, setOrganizationFilter] = useState('');
  const [selectedTask, setSelectedTask] = useState<TFullTaskData | null>(null);
  const [isLoadingTask, setIsLoadingTask] = useState(true);

  const [taskContext, setTaskContext] = useState<RequestGetByIdResponseDTO | null>(null);
  const [isLoadingTaskContext, setIsLoadingTaskContext] = useState(false);

  const [selectedGroup, setSelectedGroup] = useState<TaskGroup | null>(null);
  const [taskGroups, setTaskGroups] = useState<TaskGroup[]>([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [groupsCurrentPage, setGroupsCurrentPage] = useState(1);
  const [groupsTotalPages, setGroupsTotalPages] = useState(1);
  const [tasksSummary, setTasksSummary] = useState<GetTasksSummaryResponseDTO | null>(null);
  const [tasksError, setTasksError] = useState(false);
  const [groupsError, setGroupsError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pendingNewTasks, setPendingNewTasks] = useState<TTaskItem[]>([]);
  const [newTasksCount, setNewTasksCount] = useState(0);

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sourceDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const typeDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const statusDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const longPollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isLongPollingActiveRef = useRef(false);
  const lastUpdateCheckRef = useRef<Date>(new Date());
  const tasksRef = useRef<TTaskItem[]>([]);
  const pendingNewTasksRef = useRef<TTaskItem[]>([]);
  const userNamesRef = useRef<Record<string, string>>({});

  const user = useAppSelector((state) => state.authenticator.user);
  const selectedTaskId = searchParams.get('taskId');

  const currentUserTeamMember = useMemo(() => {
    if (!user?.info?.id || teamMembers.length === 0) return null;
    return teamMembers.find((member) => member.userId === user.info.id);
  }, [user, teamMembers]);

  const availableAssignedUsers = useMemo<TeamMemberOption[]>(() => {
    if (teamMembers.length === 0) return [];
    return teamMembers.map((member) => ({
      id: member.userId,
      name: member.name,
      email: member.email,
    }));
  }, [teamMembers]);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      setIsLoadingTeamMembers(true);
      try {
        const members = await getTeamMembers();
        setTeamMembers(members);
      } catch (error) {
        console.error('Error fetching team members:', error);
      } finally {
        setIsLoadingTeamMembers(false);
      }
    };
    fetchTeamMembers();
  }, []);

  useEffect(() => {
    if (!user?.info?.id || isLoadingTeamMembers || filtersInitialized) return;
    const currentUserSquads = currentUserTeamMember?.squads || [];
    const defaultSources = getDefaultSourceFiltersBySquads(currentUserSquads);
    setSourceFilter(defaultSources);
    setFilters((prev) => ({
      ...prev,
      source: defaultSources,
      assignedUserId: 'me_or_unassigned',
    }));
    setFiltersInitialized(true);
  }, [user, currentUserTeamMember, isLoadingTeamMembers, filtersInitialized]);

  useEffect(() => {
    const fetchTaskDetails = async () => {
      if (!selectedTaskId) {
        setSelectedTask(null);
        return;
      }
      setIsLoadingTask(true);
      setIsLoadingTaskContext(true);
      try {
        const fullTask = await getTaskById(selectedTaskId);
        const mappedTask = TasksByIdMapper(fullTask);

        setSelectedTask(mappedTask);
        setIsLoadingTask(false);

        const context = await getTaskContext(
          (fullTask?.details as { requestId?: string })?.requestId,
          fullTask?.source,
        );
        setTaskContext(context ?? null);
      } catch (error) {
        console.error('Error fetching task details:', error);
        setSelectedTask(null);
        setTaskContext(null);
      } finally {
        setIsLoadingTask(false);
        setIsLoadingTaskContext(false);
      }
    };
    fetchTaskDetails();
  }, [selectedTaskId]);

  const fetchTasksSummary = useCallback(async () => {
    try {
      const summary = await getTasksSummary({ ...filters, startDate, endDate });
      setTasksSummary(summary);
    } catch (error) {
      console.error('Error fetching tasks summary:', error);
    }
  }, [startDate, endDate, filters]);

  const fetchTasks = useCallback(async () => {
    setTasks([]);
    setTasksError(false);
    setIsLoading(true);
    const startTime = Date.now();
    const minLoadingTime = 300;
    try {
      const response = await getTasks({ ...filters, startDate, endDate }, currentPage);
      if (response) {
        setTasks(response.items);
        setTotalPages(response.totalPages);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setTasksError(true);
    } finally {
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
      setTimeout(() => setIsLoading(false), remainingTime);
    }
  }, [currentPage, startDate, endDate, filters]);

  const fetchGroups = useCallback(async () => {
    setIsLoadingGroups(true);
    setGroupsError(false);
    setSelectedGroup(null);
    try {
      const response = await getTasksGroups({ ...filters, startDate, endDate }, groupsCurrentPage);

      const groups: TaskGroup[] = response.items.map((group: TaskGroupItemDTO) => ({
        name: group.name ?? '',
        message: group.message,
        source: group.source as TTaskSource,
        type: group.type,
        organizationId: group.organizationId,
        statusCounts: calculateStatusCounts(group.tasks),
        totalTasks: group.count,
        tasks: group.tasks,
      }));

      groups.sort((a, b) => b.totalTasks - a.totalTasks);

      setTaskGroups(groups);
      setGroupsTotalPages(response.totalPages);
    } catch (error) {
      console.error('Error fetching task groups:', error);
      setGroupsError(true);
      setTaskGroups([]);
    } finally {
      setIsLoadingGroups(false);
    }
  }, [groupsCurrentPage, startDate, endDate, filters]);

  const updateTaskInGroups = useCallback((taskId: string, updates: Partial<GroupTaskItemDTO>) => {
    const updateGroup = (group: TaskGroup): TaskGroup => {
      const taskIndex = group.tasks.findIndex((t) => t.id === taskId);
      if (taskIndex === -1) return group;

      const updatedTasks = [...group.tasks];
      updatedTasks[taskIndex] = { ...updatedTasks[taskIndex], ...updates };

      return {
        ...group,
        tasks: updatedTasks,
        statusCounts: calculateStatusCounts(updatedTasks),
      };
    };

    setTaskGroups((prev) => prev.map(updateGroup));
    setSelectedGroup((prev) => (prev ? updateGroup(prev) : null));
  }, []);

  const updateBulkTasksInGroups = useCallback((taskIds: string[], updates: Partial<GroupTaskItemDTO>) => {
    const taskIdSet = new Set(taskIds);

    const updateGroup = (group: TaskGroup): TaskGroup => {
      const hasMatch = group.tasks.some((t) => taskIdSet.has(t.id));
      if (!hasMatch) return group;

      const updatedTasks = group.tasks.map((t) => (taskIdSet.has(t.id) ? { ...t, ...updates } : t));

      return {
        ...group,
        tasks: updatedTasks,
        statusCounts: calculateStatusCounts(updatedTasks),
      };
    };

    setTaskGroups((prev) => prev.map(updateGroup));
    setSelectedGroup((prev) => (prev ? updateGroup(prev) : null));
  }, []);

  const handleGroupsPageChange = useCallback((page: number) => {
    setGroupsCurrentPage(page);
  }, []);

  useEffect(() => {
    tasksRef.current = tasks;
  }, [tasks]);

  useEffect(() => {
    pendingNewTasksRef.current = pendingNewTasks;
  }, [pendingNewTasks]);

  const startLongPolling = useCallback(() => {
    if (isLongPollingActiveRef.current) return;
    isLongPollingActiveRef.current = true;

    const pollTasks = async () => {
      if (!isLongPollingActiveRef.current) return;
      try {
        const updateStartDate = new Date(lastUpdateCheckRef.current);
        lastUpdateCheckRef.current = new Date();
        const response = await getTasks({ ...filters, startDate, endDate, updateStartDate }, currentPage);

        if (response && response.items.length > 0) {
          const currentTasks = tasksRef.current;
          const currentPending = pendingNewTasksRef.current;
          const existingTaskIds = new Set(currentTasks.map((t) => t.id));
          const pendingTaskIds = new Set(currentPending.map((t) => t.id));
          const newTasks: TTaskItem[] = [];
          const updatedTasksMap = new Map<string, TTaskItem>();

          response.items.forEach((task) => {
            if (existingTaskIds.has(task.id) || pendingTaskIds.has(task.id)) {
              updatedTasksMap.set(task.id, task);
            } else {
              const taskCreatedAt = new Date(task.createdAt);
              if (taskCreatedAt >= updateStartDate) {
                newTasks.push(task);
              }
            }
          });

          if (updatedTasksMap.size > 0) {
            setTasks((prev) => prev.map((t) => (updatedTasksMap.has(t.id) ? updatedTasksMap.get(t.id)! : t)));
            setPendingNewTasks((prev) => prev.map((t) => (updatedTasksMap.has(t.id) ? updatedTasksMap.get(t.id)! : t)));
          }

          if (newTasks.length > 0) {
            setPendingNewTasks((prev) => {
              const existingIds = new Set(prev.map((t) => t.id));
              const additions = newTasks.filter((t) => !existingIds.has(t.id));
              if (additions.length > 0) {
                setNewTasksCount((c) => c + additions.length);
                return [...additions, ...prev];
              }
              return prev;
            });
          }
        }
      } catch (error) {
        console.error('Error in long polling:', error);
      } finally {
        if (isLongPollingActiveRef.current) {
          longPollingTimeoutRef.current = setTimeout(pollTasks, 5000);
        }
      }
    };

    longPollingTimeoutRef.current = setTimeout(pollTasks, 5000);
  }, [filters, startDate, endDate, currentPage]);

  const stopLongPolling = useCallback(() => {
    isLongPollingActiveRef.current = false;
    if (longPollingTimeoutRef.current) {
      clearTimeout(longPollingTimeoutRef.current);
      longPollingTimeoutRef.current = null;
    }
  }, []);

  const handleUpdateTaskStatus = useCallback(
    async (taskId: string, status: Status) => {
      try {
        setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, status: status } : t)));
        updateTaskInGroups(taskId, { status });
        const res = await updateTaskStatus(taskId, status);
        setTasks((prev) =>
          prev.map((t) => (t.id === taskId ? { ...t, status: status, assignedUserId: res.assignedUserId } : t)),
        );
        updateTaskInGroups(taskId, { status, assignedUserId: res.assignedUserId });
      } catch (error) {
        console.error('Error updating task status:', error);
      }
    },
    [updateTaskInGroups],
  );

  const handleUpdateTaskUser = useCallback(
    async (taskId: string, userId?: string | null, toActualUser?: boolean) => {
      try {
        const targetUserId = toActualUser ? user.info.id : userId;
        setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, assignedUserId: targetUserId } : t)));
        updateTaskInGroups(taskId, { assignedUserId: targetUserId });
        await updateTaskAssignedUser(taskId, targetUserId);
      } catch (error) {
        console.error('Error updating task assigned user:', error);
      }
    },
    [user, updateTaskInGroups],
  );

  const handleTaskClick = useCallback(
    (task: TTaskItem) => {
      if (searchParams.get('taskId') === task.id) {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('taskId');
        setSearchParams(newParams);
      } else {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('taskId', task.id);
        setSearchParams(newParams);
      }
    },
    [searchParams, setSearchParams],
  );

  const handleFilterChange = useCallback((property: keyof TTaskItem, value: string | string[]) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (property === 'assignedUserId') {
        if (typeof value === 'string' && value) {
          next.assignedUserId = value === 'unassigned' ? null : value;
        } else {
          delete next.assignedUserId;
        }
      }
      return next;
    });
  }, []);

  const handleSortChange = useCallback((sortOrder: 'asc' | 'desc') => {
    setFilters((prev) => ({ ...prev, sortBy: 'createdAt', sortOrder }));
  }, []);

  const handleMessageFilterChange = useCallback((value: string) => {
    setMessageFilter(value);
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, message: value.trim() || undefined }));
    }, 500);
  }, []);

  const handleSourceFilterChange = useCallback((value: string[]) => {
    setSourceFilter(value);
    if (sourceDebounceRef.current) clearTimeout(sourceDebounceRef.current);
    sourceDebounceRef.current = setTimeout(() => {
      setFilters((prev) => {
        if (value.length > 0) {
          return { ...prev, source: value };
        }
        const next = { ...prev };
        delete next.source;
        return next;
      });
    }, 500);
  }, []);

  const handleStatusFilterChange = useCallback((value: string[]) => {
    setStatusFilter(value);
    if (statusDebounceRef.current) clearTimeout(statusDebounceRef.current);
    statusDebounceRef.current = setTimeout(() => {
      setFilters((prev) => {
        if (value.length > 0) {
          return { ...prev, status: value };
        }
        const next = { ...prev };
        delete next.status;
        return next;
      });
    }, 500);
  }, []);

  const handleTypeFilterChange = useCallback((value: string[]) => {
    setTypeFilter(value);
    if (typeDebounceRef.current) clearTimeout(typeDebounceRef.current);
    typeDebounceRef.current = setTimeout(() => {
      setFilters((prev) => {
        if (value.length > 0) {
          return { ...prev, type: value as TaskFilters['type'] };
        }
        const next = { ...prev };
        delete next.type;
        return next;
      });
    }, 500);
  }, []);

  const handleOrganizationFilterChange = useCallback((organizationId: string | null) => {
    if (organizationId) {
      setOrganizationFilter(organizationId);
      setFilters((prev) => ({ ...prev, organizationId }));
    } else {
      setOrganizationFilter('');
      setFilters((prev) => {
        const next = { ...prev };
        delete next.organizationId;
        return next;
      });
    }
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  const handleLoadNewTasks = useCallback(() => {
    if (pendingNewTasks.length > 0) {
      setTasks((prev) => [...pendingNewTasks, ...prev]);
      setPendingNewTasks([]);
      setNewTasksCount(0);
      lastUpdateCheckRef.current = new Date();
    }
  }, [pendingNewTasks]);

  const handleBulkUpdateTasks = useCallback(
    async (tasksPayload: { taskIds: string[]; newStatus?: string; newAssignedUserId?: string }) => {
      const { taskIds, newStatus: status, newAssignedUserId } = tasksPayload;
      const newData = { status: status, assignedUserId: newAssignedUserId };
      const cleanedNewData = Object.fromEntries(Object.entries(newData).filter(([, v]) => v !== undefined));

      const groupUpdates: Partial<GroupTaskItemDTO> = {};
      if (status) groupUpdates.status = status as Status;
      if (newAssignedUserId !== undefined) groupUpdates.assignedUserId = newAssignedUserId;
      if (Object.keys(groupUpdates).length > 0) {
        updateBulkTasksInGroups(taskIds, groupUpdates);
      }

      try {
        await updateBulkTasks(taskIds, status as Status, newAssignedUserId);
        setTasks((prev) => prev.map((t) => (taskIds.includes(t.id) ? { ...t, ...cleanedNewData } : t)));
      } catch (error) {
        console.error('Error updating task status:', error);
      }
    },
    [updateBulkTasksInGroups],
  );

  const handleCreateComment = useCallback(
    async (content: string) => {
      if (!selectedTaskId) return;
      try {
        const newComment = await createTaskComment(selectedTaskId, content);

        setSelectedTask((prev) => {
          if (!prev) return prev;
          return { ...prev, comments: [newComment, ...(prev.comments || [])] };
        });
      } catch (error) {
        console.error('Error creating comment:', error);
        throw error;
      }
    },
    [selectedTaskId],
  );

  const handleUpdateComment = useCallback(
    async (commentId: string, content: string) => {
      if (!selectedTaskId) return;
      try {
        const updated = await updateTaskComment(selectedTaskId, commentId, content);

        setSelectedTask((prev) => {
          if (!prev) return prev;
          return { ...prev, comments: (prev.comments || []).map((c) => (c.id === commentId ? updated : c)) };
        });
      } catch (error) {
        console.error('Error updating comment:', error);
        throw error;
      }
    },
    [selectedTaskId],
  );

  const handleDeleteComment = useCallback(
    async (commentId: string) => {
      if (!selectedTaskId) return;
      try {
        await deleteTaskComment(selectedTaskId, commentId);
        setSelectedTask((prev) => {
          if (!prev) return prev;
          return { ...prev, comments: (prev.comments || []).filter((c) => c.id !== commentId) };
        });
      } catch (error) {
        console.error('Error deleting comment:', error);
        throw error;
      }
    },
    [selectedTaskId],
  );

  const clearSelectedTask = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('taskId');
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const handleGroupSelect = useCallback(
    (group: TaskGroup | null) => {
      setSelectedGroup(group);
      clearSelectedTask();
    },
    [clearSelectedTask],
  );

  const handleCreateRerun = useCallback(async () => {
    if (!selectedTaskId) return;
    try {
      await createTaskRerun(selectedTaskId);
    } catch (error) {
      console.error('Error creating rerun:', error);
      throw error;
    }
  }, [selectedTaskId]);

  useEffect(() => {
    setCurrentPage(1);
    setGroupsCurrentPage(1);
    setPendingNewTasks([]);
    setNewTasksCount(0);
    stopLongPolling();
    lastUpdateCheckRef.current = new Date();
  }, [startDate, endDate, filters, stopLongPolling]);

  useEffect(() => {
    fetchTasks();
    fetchTasksSummary();
  }, [fetchTasks, fetchTasksSummary]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  useEffect(() => {
    lastUpdateCheckRef.current = new Date();
    startLongPolling();
    return () => stopLongPolling();
  }, [startLongPolling, stopLongPolling]);

  useEffect(() => {
    const teamMemberNames: Record<string, string> = {};
    teamMembers.forEach((member) => {
      teamMemberNames[member.userId] = member.name;
    });
    setUserNames((prev) => ({ ...teamMemberNames, ...prev }));
  }, [teamMembers]);

  useEffect(() => {
    userNamesRef.current = userNames;
  }, [userNames]);

  useEffect(() => {
    const fetchUserNames = async () => {
      const userIds = new Set<string>();

      tasks.forEach((task) => {
        if (task.assignedUserId) {
          userIds.add(task.assignedUserId);
        }
      });

      if (selectedTask?.comments) {
        selectedTask.comments.forEach((comment) => {
          if (comment.createdByUserId) {
            userIds.add(comment.createdByUserId);
          }
        });
      }

      const uniqueUserIds = Array.from(userIds);

      const missingUserIds = uniqueUserIds.filter((id) => !userNamesRef.current[id]);

      if (missingUserIds.length > 0) {
        try {
          const newUserNames = await getUsersNames(missingUserIds, userNamesRef.current);
          setUserNames(newUserNames);
        } catch (error) {
          console.error('Error fetching user names:', error);
        }
      }
    };

    if (tasks.length > 0 || selectedTask?.comments) {
      fetchUserNames();
    }
  }, [tasks, selectedTask?.comments]);

  useEffect(() => {
    userNamesRef.current = userNames;
  }, [userNames]);

  useEffect(() => {
    const fetchUserNames = async () => {
      const userIds = new Set<string>();

      tasks.forEach((task) => {
        if (task.assignedUserId) {
          userIds.add(task.assignedUserId);
        }
      });

      if (selectedTask?.comments) {
        selectedTask.comments.forEach((comment) => {
          if (comment.createdByUserId) {
            userIds.add(comment.createdByUserId);
          }
        });
      }

      const uniqueUserIds = Array.from(userIds);

      const missingUserIds = uniqueUserIds.filter((id) => !userNamesRef.current[id]);

      if (missingUserIds.length > 0) {
        try {
          const newUserNames = await getUsersNames(missingUserIds, userNamesRef.current);
          setUserNames(newUserNames);
        } catch (error) {
          console.error('Error fetching user names:', error);
        }
      }
    };

    if (tasks.length > 0 || selectedTask?.comments) {
      fetchUserNames();
    }
  }, [tasks, selectedTask?.comments]);

  useEffect(() => {
    const fetchOrganizations = async () => {
      setIsLoadingOrganizations(true);
      try {
        const orgNames = await getOrganizationsNames();
        setOrganizationNames(orgNames);
      } catch (error) {
        console.error('Error fetching organizations:', error);
      } finally {
        setIsLoadingOrganizations(false);
      }
    };
    fetchOrganizations();
  }, []);

  useEffect(() => {
    return () => {
      stopLongPolling();
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
      if (sourceDebounceRef.current) clearTimeout(sourceDebounceRef.current);
      if (typeDebounceRef.current) clearTimeout(typeDebounceRef.current);
      if (statusDebounceRef.current) clearTimeout(statusDebounceRef.current);
    };
  }, [stopLongPolling]);

  const value = useMemo<TasksContextValue>(
    () => ({
      tasks,
      isLoading,
      filters,
      messageFilter,
      sourceFilter,
      typeFilter,
      statusFilter,
      organizationFilter,
      handleFilterChange,
      handleMessageFilterChange,
      handleSourceFilterChange,
      handleTypeFilterChange,
      handleStatusFilterChange,
      handleOrganizationFilterChange,
      handleSortChange,
      startDate,
      endDate,
      setStartDate,
      setEndDate,
      currentPage,
      totalPages,
      handlePageChange,
      handleUpdateTaskStatus,
      handleUpdateTaskUser,
      handleTaskClick,
      handleBulkUpdateTasks,
      selectedTaskId,
      selectedTask,
      isLoadingTask,
      taskContext,
      isLoadingTaskContext,
      handleCreateComment,
      handleUpdateComment,
      handleDeleteComment,
      handleCreateRerun,
      userNames,
      teamMembers: availableAssignedUsers,
      organizationNames,
      isLoadingOrganizations,
      tasksSummary,
      tasksError,
      groupsError,
      retryTasks: fetchTasks,
      retryGroups: fetchGroups,
      newTasksCount,
      handleLoadNewTasks,
      selectedGroup,
      handleGroupSelect,
      clearSelectedTask,
      taskGroups,
      isLoadingGroups,
      groupsCurrentPage,
      groupsTotalPages,
      handleGroupsPageChange,
    }),
    [
      tasks,
      isLoading,
      filters,
      messageFilter,
      sourceFilter,
      typeFilter,
      statusFilter,
      organizationFilter,
      handleFilterChange,
      handleMessageFilterChange,
      handleSourceFilterChange,
      handleTypeFilterChange,
      handleStatusFilterChange,
      handleOrganizationFilterChange,
      handleSortChange,
      startDate,
      endDate,
      currentPage,
      totalPages,
      handlePageChange,
      handleUpdateTaskStatus,
      handleUpdateTaskUser,
      handleTaskClick,
      handleBulkUpdateTasks,
      selectedTaskId,
      selectedTask,
      isLoadingTask,
      taskContext,
      isLoadingTaskContext,
      handleCreateComment,
      handleUpdateComment,
      handleDeleteComment,
      handleCreateRerun,
      userNames,
      availableAssignedUsers,
      organizationNames,
      isLoadingOrganizations,
      tasksSummary,
      tasksError,
      groupsError,
      fetchTasks,
      fetchGroups,
      newTasksCount,
      handleLoadNewTasks,
      selectedGroup,
      handleGroupSelect,
      clearSelectedTask,
      taskGroups,
      isLoadingGroups,
      groupsCurrentPage,
      groupsTotalPages,
      handleGroupsPageChange,
    ],
  );

  return <TasksContext.Provider value={value}>{children}</TasksContext.Provider>;
};
